import type { PipelineStatus } from '@@/packages/shared/types/models/connection';
import type { SupplierListReviewState } from '@@/packages/shared/types/models/connectionReview';

export default {
  en: {
    unclaimedSupplier: 'Unclaimed',
    addSupplier: 'Add supplier',
    reviewStates: {
      not_reviewed: 'Not reviewed',
      in_review: 'In review',
      reviewed: 'Reviewed',
      reviewed_approved: 'Reviewed (approved)',
      reviewed_rejected: 'Reviewed (rejected)',
      needs_re_review: 'Needs re-review',
    } satisfies Record<SupplierListReviewState, string>,
    answer: {
      answered: 'Answered',
      unanswered: 'Unanswered',
      answeredClient: 'You answered',
      last_modified: 'Last modified {0}',
    },
    info: {
      country: 'Country',
      companyNumber: 'Company number',
      notProvided: 'Not provided',
      sector: 'Sector',
      tradingNames: 'Trading names',
      unclaimed: "This information hasn't been verified by the supplier",
      incompleteAssessment: 'This supplier has not yet completed their assessment.',
      incompleteAssessmentClient: 'It looks like you have not yet completed your assessment.',
      incompleteAssessmentNote: 'You will be notified when they do so.',
      incompleteAssessmentNoteClient: 'You will be able to see your compliance when you do so.',
      noFilteredAnswers: 'No answers match the filters applied',
      noFilteredAnswersNote: 'Change the filters to show some answers',
      lastUpdatedTooltip: 'The last time this supplier updated an answer.',
      lastUpdatedAt: 'Last updated: {date}',
      unclaimedNotConnectedTitle: 'No one from this supplier is active on Risk Ledger yet',
      unclaimedNotConnectedDesc:
        'They will be included in your list of suppliers but you may miss some data',
      claimedNotConnectedTitle:
        'Add this supplier to request access to their assessment and add notes, risks and documents',
      claimedNotConnectedDesc: "We'll let you know once the supplier has accepted your request",
      unverified: 'Unclaimed',
      addsupplier: 'Add supplier',
      trialBanner: {
        title: "You've reached the limit of suppliers in the free trial.",
        description: 'Book a demo to learn about our paid plans with unlimited suppliers.',
        cta: 'Book a demo',
      },
      notFound: {
        title: 'Supplier not found',
        description:
          'This supplier is no longer available. Return to your suppliers list to continue managing suppliers.',
        cta: 'View suppliers',
      },
      restricted: {
        title: "You can't view this supplier",
        description:
          'You can only access your assigned suppliers. Ask one of your admins to assign you to this supplier, or to give you access to all suppliers.',
        cta: 'View suppliers',
      },
    },
    stats: {
      compliance: 'Compliance',
      score: 'Score',
      complianceScore: 'Compliance score',
      controls: 'Controls',
      overrides: 'Overrides',
      compliant: 'Compliant',
      nonComplianceApplied: 'Non-compliances',
      compliancesApplied: 'Compliances',
      remediations: 'Remediations',
      remediationRequests: 'Requests',
      remediationResponses: 'Responses',
      risks: 'Risks',
      complete: 'Complete',
      openRisks: 'Open',
      closedRisks: 'Closed',
      myReview: 'Compliance overview',
      closedRisksIn: 'Closed risk | Closed risks',
      approvalRate: 'Approval rate',
      viewHere: 'View here',
      findings: {
        title: 'Monitoring findings',
        monitoring: 'Monitoring',
        total: 'Findings',
        assetCountTitle: 'Assets',
        diveDeepTitle: 'Want to deep dive into findings?',
        diveDeepDesc:
          'Gain extra external data points for this supplier without the need for assessments',
        severity: {
          critical: 'Critical',
          high: 'High',
          medium: 'Medium',
          low: 'Low',
          info: 'Info',
        },
        modal: {
          monitoring: 'Monitoring',
          title: "Deep dive into {name}'s scanned assets",
          description:
            'As a supplier within your network, we identified assets and scanned them. This pre-analysis provides foundational insights into their presence within your supplier network',
          keyBenefits: 'Key benefits',
          benefit1Key: 'Complete Asset Visibility:',
          benefit1:
            '{0} Gain a consolidated view of all scanned assets within your network, providing a comprehensive inventory.',
          benefit2Key: 'Proactive Risk Mitigation:',
          benefit2:
            '{0} Identify potential security infractions and vulnerabilities early, enabling timely intervention before they become critical threats.',
          benefit3Key: 'Flexible Scan Capabilities:',
          benefit3:
            '{0} Leverage a diverse array of scan types to tailor assessments to specific asset types and security requirements, ensuring thorough coverage.',
          requestDemoNow: 'Request a demo now to enable External Monitoring.',
          requestADemo: 'Request a demo now',
          notNow: 'Not now',
          successToast: 'We have received your interest and we will be in contact soon',
          errorToast: 'Failed registering interest. Try again soon.',
        },
      },
      policyImpact: 'Policies impacting this supplier',
      nonCompliantCount: '{count} non-compliant',
      notScored: 'Not scored',
      policyNotes: 'Policy notes',
      policyCategorisation: 'Categorisation linked to this policy',
      byDomain: {
        nonCompliant: '{count} non-compliant',
        percentageCompliant: '{percentage}% compliant',
        notScored: 'Not Scored',
      },
    },
    policy: {
      required: 'Policy requires',
      clientRequired: 'Client requires',
      notRequired: 'Control not required',
      yes: 'Yes',
      no: 'No',
      orMore: '{0} or more',
      orLess: '{0} or less',
      policyRequirementsTitle: 'Policy requirements',
      noPolicyRequirements: 'No policy requirements',
      requiredByPoliciesTooltip:
        'Not required by any policies | Required by one policy | Required by {n} policies',
      requirement: 'Requires: {requirement}',
    },
    buttons: {
      cancel: 'Cancel',
      confirm: 'Confirm',
      editAnswer: 'Edit answer',
      answer: 'Answer',
      actions: 'Actions',
    },
    tags: {
      criticality: 'Criticality',
      confidentiality: 'Confidentiality',
      pii: 'PII',
      editCriticality: 'Edit criticality',
      editConfidentiality: 'Edit confidentiality',
      editPii: 'Edit PII',
      set_tag_failed: 'Failed to set tag',
    },
    pipelineStatus: {
      title: 'Pipeline status',
      edit: 'Edit pipeline status',
      unset: 'Not set',
      error: 'Failed to update pipeline status. Please try again.',
      label: {
        in_tender: 'In tender',
        pre_contract: 'Pre-contract',
        in_contract: 'In contract',
        other: 'Other',
      } satisfies Record<PipelineStatus, string>,
    },
    overrideReason: {
      nonComplianceTitle: 'Non-compliance applied',
      exemptionAppliedTitle: 'Exemption applied',
      nonComplianceDesc:
        'Applying non-compliance will override the compliance status of this answer, making it non-compliant until removed.',
      exemptionDesc:
        'Applying exemption will override the compliance status of this answer, making it compliant until removed.',
      nonComplianceDescMulti:
        'Applying non-compliance will override the compliance status of these answers, making them non-compliant until removed.',
      exemptionDescMulti:
        'Applying exemptions will override the compliance status of these answers, making them compliant until removed.',
      title: 'Please provide a reason:',
      nonCompliancePlaceholder: 'This answer is non-compliant because...',
      exemptionPlaceholder: 'This answer is compliant because...',
      defaultError: 'Could not apply override. Please try again.',
      reasonRequired: 'Enter a reason',
      buttons: {
        applyNonCompliance: 'Apply non-compliance',
        applyExemption: 'Apply exemption',
        apply: 'Apply',
        remove: 'Cancel override',
      },
    },
    complianceProgress: {
      title: 'Compliance',
      incompleteAssessment: 'This supplier has not yet completed their assessment.',
      incompleteAssessmentNote: 'You will be notified when they do so.',
      complete: 'Complete',
      lastUpdatedAt: 'Last updated: {date}',
    },
    overview: {
      maybeInaccurate:
        "This supplier's compliance score might not be accurate as they have yet to fill out all the controls you have required",
      noComplianceInfoTitle: 'This supplier has no policies impacting their score',
      noComplianceInfoText:
        "Change the suppliers' categorisations or labels to add a relevant policy",
      status: 'Status',
      client: 'Client',
      policiesApplied: 'Policies applied',
      policiesNoneAppliedTitle: 'No policies applied',
      policiesNoneAppliedText:
        'Add categorisations or labels to this supplier to apply relevant policies',
      policiesNoneActiveTitle: 'No active policies',
      policiesNoneActiveText:
        'Any policies set will take effect once the supplier claims their profile and accepts the connection. You can view and modify them at any time.',
      notScored: 'Not scored',
      complianceByDomain: 'Compliance by domain',
      complianceByDomainKey: 'Colour key',
      errorClient: 'Failed to load client. Please try again.',
      errorClientDowngradedTitle: 'This connection is no longer active',
      errorClientDowngraded:
        "You no longer have full access to this client's profile. If you have a pending connection request from this client, accept it to restore access — otherwise, ask them to send you a new connection request.",
      errorSupplier: 'Failed to load supplier. Please try again.',
      clientBanner: "You're currently viewing your compliance against {name}'s policies.",
      assignEmpty: 'Nobody yet.',
      assignYourself: 'Assign yourself',
      assignUsers: 'Select users...',
      cancel: 'Cancel',
      save: 'Save',
      assignedTo: 'Assigned to',
      editAssignedTo: 'Edit assigned users',
      errorAssignment: 'There was an error assigning users',
      assessmentStatus: 'Assessment status',
      openRisk: 'Open risk',
      viewClientNetwork: 'View in network map',
      viewCommunityNetwork: 'View in community map',
      downloadLastReview: 'Download last review (PDF)',
      createCommunityPost: 'Create community post',
      viewCommunityPosts: 'View community posts',
      sharingSettings: 'Community sharing settings',
      noPoliciesApplied1: '{org} have not yet applied their policies to your profile..',
      noPoliciesApplied2:
        "Once policies have been applied, you'll be able to see a breakdown of your compliance by domain and question.",
      noOneActiveTitle: 'No one from this supplier is active on Risk Ledger yet',
      noOneActiveText: 'They will be included in your list of suppliers but you may miss some data',
      noOneActiveCta: 'Invite to Risk Ledger',
      noOneActivePendingInviteTitle: "We've invited this supplier to join Risk Ledger",
      noOneActivePendingInviteText:
        'After {email} accepts your invite they will be able to complete their assessment',
      cancelInviteModal: {
        title: 'Cancel supplier invite?',
        description:
          'This will stop the supplier from joining Risk Ledger through this invite. You can send a new invite later from the supplier profile.',
        keepInvite: 'Keep invite',
        cancelInvite: 'Cancel invite',
      },
      activePendingConnectionRequestTitle: '{name} has requested access to this supplier',
      activePendingConnectionRequestText:
        'After {orgName} accepts the request you will be able to access their full profile',
      pendingReceivedConnectionRequestTitle: 'Pending connection request',
      pendingReceivedConnectionRequestDesc:
        'This supplier has already sent you a connection request. Accept it in the Action Centre to connect',
      pendingReceivedConnectionRequestCta: 'View request',
      activeNoConnectionRequestTitle:
        'Add this supplier to request access to their assessment and add notes, risks and documents',
      activeNoConnectionRequestDesc:
        "We'll let you know once the supplier has accepted your request",
      basicReview: 'Basic review',
      // Each card reads as a verb while its section is empty, and as the section
      // name plus a count once there is something to go and see.
      basicReviewCards: {
        notesEmpty: 'Add private note',
        notesEmptySubtitle: 'No notes yet',
        notes: 'Private notes',
        notesCount: '{n} note | {n} notes',
        risksEmpty: 'Open a risk',
        risksEmptySubtitle: 'No open risks',
        risks: 'Risks',
        risksCount: '{n} open risk | {n} open risks',
        documentsEmpty: 'Upload documents',
        documentsEmptySubtitle: 'No documents yet',
        documents: 'Documents',
        documentsCount: '{n} document | {n} documents',
      },
      requestAccess: 'Request access',
      errorConnectionPrivacy: 'Failed to update connection privacy. Please try again.',
      privateConnectionOffDescription:
        'Your connection to {name} is visible to other organisations on the network and communities.',
      privateConnectionOnDescription:
        'Your connection to {name} is not visible to other organisations on the network and communities.',
      privateConnection: 'Private connection',
      securityCertifications: 'Security certifications',
      publicData: {
        title: 'Public data',
        found: 'Found {date}',
        discover: 'Find public supplier data',
        discoverDescription:
          'Search public sources for trust centres, certifications and other security information to support your supplier review.',
        discoverCta: 'Get public data',
        searching: 'Discovering public data...',
        searchingDescription:
          "We are searching for this supplier's publicly available security information. This may take a moment.",
        certifications: 'Certifications',
        resources: 'Resources',
        viewAll: 'View all',
        notFound: 'Not found',
      },
      certificates: {
        valid: 'Valid',
        expired: 'Expired',
        tooltip: {
          expiryDate: 'Expiry date',
          accreditationBody: 'Accreditation body',
          certificationBody: 'Certification Body',
          source: 'Source Type',
          certificateLink: 'Certification link',
          sourceManual: 'Manual',
          sourceIafCertSearch: 'IAF',
          sourceIasmeCertSearch: 'IASME',
          sourceWebScraped: 'Web scraped',
        },
        discover: 'Discover supplier security certifications',
        discoverDescription: 'See which security certifications your supplier maintains',
        discoverCta: 'Start discovery',
        searching: 'Discovering certifications...',
        searchingDescription:
          'We are checking if this supplier holds any certifications. This may take a moment.',
        searchingMore: 'Discovering more certifications...',
        searchingMoreDescription: 'We are checking if this supplier holds more certifications',
        notFound: 'No certifications discovered',
        notFoundDescription:
          "We couldn't find any certifications for this supplier yet. They may be under a different name, like a parent company or trading name.",
        errorMessage: "We can't discover certifications right now",
        errorDescription:
          'Certification discovery is unavailable right now. Check back later to see your results.',
        reportMissing: 'Flag missing certifications',
        reportMissingMessage:
          "I am expecting {name} to have a certification that I don't see here.",
        enableModal: {
          title: 'What security data points do you need to review unclaimed profiles?',
          description:
            'We need your input to prioritise data surfacing. ISO 27001 and Cyber Essentials certifications will launch first.',
          optionsLabel: 'Select the most important:',
          validation: {
            noneAndAllAbove: 'Cannot select these two options together',
            noneMustBeOnly: 'None of the above cannot be selected with other options',
            allAboveMustBeOnly:
              'All of the above are helpful, but not essential cannot be selected with other options',
            empty: 'In order to submit, you need to tick at least 1 answer',
          },
          options: {
            dataProtection: 'Data protection & information security policies',
            adverseMedia: '(Historical) adverse media',
            businessInformation: 'Business information (security team sizes, privacy policy, T&Cs)',
            dataResidency: 'Data residency & subprocessors',
            additionalCertificates: 'Additional certifications (SOC2, Business Continuity)',
            allAbove: 'All of the above are helpful, but not essential',
            none: 'None of the above',
          },
          submitButton: 'Submit',
          skipButton: 'Skip',
        },
      },
    },
    controlReferencePopover: {
      answer: 'Answer',
      notes: 'Notes',
      evidence: 'Evidence',
      compliance: 'Compliance',
      noAnswer: 'No answer yet for this control.',
      productAnswer: 'In relation to {productName}:',
      plusProductAnswers: 'Plus {count} product answer | Plus {count} product answers',
    },
    riskReferencePopover: {
      openedBy: 'Opened by {name} on {date}',
    },
    profileSummary: {
      generateButton: 'Summarise profile',
      generateDescription:
        "Generate an AI overview of this supplier's security profile, highlighting strengths and weaknesses.",
      title: 'Security overview',
      pdfTitle: 'Security overview for {name}',
      pdfDisclaimer:
        'AI-generated content may be inaccurate. Verify important details against source data',
      errorMessage: 'Something went wrong generating the summary.',
      staleMessage: 'This summary is taking longer than expected. You can try generating it again.',
      retryButton: 'Retry',
      helpful: 'Helpful',
      notHelpful: 'Not helpful',
      feedbackThanks: 'Feedback received.',
      strongPoints: 'Strong points',
      weakPoints: 'Areas for improvement',
      risks: 'Risks',
      remediations: 'Remediations',
      regenerateButton: 'This summary may be out of date. Click to regenerate.',
      exportPDF: 'Export as PDF',
      copy: 'Copy summary',
      copied: 'Copied',
      disclaimer: 'AI-generated content may be inaccurate, check important details',
      showMore: 'Show more',
      showLess: 'Show less',
      showMoreAlt: 'Show more of the security overview',
      showLessAlt: 'Show less of the security overview',
    },
    removeConnection: {
      supplier: 'Remove supplier',
      client: 'Remove client',
      title: 'Remove {name}?',
      message: 'Before continuing, confirm if you would like to remove {name} as a {type}.',
      defaultError: 'Failed to remove connection. Please try again.',
      reasonPlaceholder: 'Removal reason...',
      reasonQuestion: 'Why are you removing this connection?',
      messageWarning: 'This message will be sent to {name}.',
      whatWillBeDeleted: 'What will be deleted',
      whatHappensNext: 'What happens next',
      privateNotes: 'Your private notes',
      privateNotesWithControls: 'Your private notes, including notes on controls',
      documents: 'Documents you uploaded, and their renewal reminders',
      reviews: 'Connection reviews, approval status and assessment scope',
      remediations: 'Remediation requests',
      discussions: "You'll lose access to your discussions with {name}",
      risks: 'Risks you raised are kept, marked as no longer connected',
      irreversible: "This can't be undone. Reconnecting won't restore this data.",
    },
    assessment: {
      compliant: 'Compliant',
      nonCompliant: 'Not compliant',
      notScored: 'Not scored',
      required: 'Required',
      notRequired: 'Not required',
      openRisks: 'Open risks',
      noOpenRisks: 'No open risks',
      searchControls: 'Search controls and answers...',
      sortBy: 'Sort by',
      filterBy: 'Filter by',
      clearFilters: 'Reset',
      domainControl: 'Domain / control',
      lastModified: 'Last modified',
      description: 'Description',
      exemptionApplied: 'Exemption applied',
      nonComplianceApplied: 'Non-compliance applied',
      remediationPending: 'Remediation pending',
      remediationResponded: 'Remediation responded',
      defaultError: 'Failed to load assessment. Please try again.',
      filter: {
        title: 'Keyboard shortcuts',
        text: 'Use the up and down keys to move between answers.',
        sections: {
          requirement: 'Requirement',
          compliance: 'Compliance',
          override: 'Overrides',
          remediation: 'Remediation',
          risk: 'Risk',
          product: 'Product',
          deprecated: 'Deprecation',
          certification: 'Certification',
          domain: 'Domain',
        },
      },
      clientBanner: {
        title: 'Read-only view of your assessment',
        description:
          "Your answers are marked against this client's requirements. To change them, go to Assessment in the main menu.",
      },
      showOOSControlsTemplate: '{0} out of scope. Show anyway',
      showOOSControls: '{n} controls | {n} control | {n} controls',
      hideOOSControlsTemplate: 'Showing {0} out of scope. Hide',
      hideOOSControls: '{n} controls | {n} control | {n} controls',
      outOfScope: 'Out of scope',
      notApplicable: 'N/A',
      domainOutOfScope: 'Domain is out of scope.<br/>To bring into scope, answer must be yes.',
      domainInScope: 'Domain is in-scope.',
      deprecated: 'Deprecated',
      nonDeprecated: 'Non deprecated',
      deprecatedControl: 'This control is deprecated.',
      inprogress: 'In progress - {percent}%',
      overdueBannerTitle: '{0} answers are over 6 months old',
      overdueBannerSubtitle:
        'Prompt them to complete their re-assessment to make sure their answers are up to date',
      openDiscussion: 'Open a discussion',
      unconfirmedAnswer: {
        title: 'Unconfirmed answer.',
        description:
          'This control question has been updated since you last reviewed your answer. Please confirm it is still accurate.',
        descriptionClient:
          'This control has been modified since the supplier last reviewed their answer. It may no longer be accurate.',
      },
      noPoliciesApplied: {
        title: 'Read-only view of your assessment',
        description:
          "{org} haven't applied their policies yet, so your compliance isn't shown. To change your answers, go to Assessment in the main menu.",
      },
      communityRemediationsFilter: 'Community remediations',
      communityRisksFilter: 'Community risks',
      productAnswerLabel: 'In relation to {product}',
    },
    tooltips: {
      reviewState: 'Review status',
      evidence: 'Evidence uploaded',
      answer: 'Answer',
      scopingAnswer: 'Scoping question answer',
      unanswered: "This control hasn't been answered",
      exemption: 'Exemption applied',
      noncompliance: 'Noncompliance applied',
      remediation: 'Remediation requested',
      remediationResponse: 'Remediation request responded',
      privateNotes: 'Has private notes',
      outOfScope: "This control's domain is out of scope",
      notApplicable: 'This control is not applicable',
      unconfirmed: 'This control has been updated and is unconfirmed',
      communityControlSignals: 'Community signals',
    },
    notes: {
      visibleLabel: 'Only visible to {0}',
      placeholder: 'Enter notes about this supplier...',
      save: 'Save',
      defaultError: 'Could not save private note. Please try again.',
      successToast: 'Successfully saved private note',
    },
    remediation: {
      popoverContent: {
        desc: 'Requesting remediation will send a notification to the supplier, asking them to remediate their answer to this control.',
        button: 'Send request',
        reason: 'Reason',
        placeholder: 'This answer needs remediating because...',
        deadline: 'Deadline',
        defaultError: 'Could not set remediation. Please try again.',
        reasonRequired: 'Enter a reason',
        deadlineRequired: 'Enter a deadline',
      },
      requested: {
        titleSupplier: 'Remediation requested',
        titleClient: 'Remediation requested by client',
        deadline: 'Deadline',
        markDone: 'Mark as done',
      },
      response: {
        titleSupplier: 'Remediated by supplier',
        titleClient: 'Remediated by you',
        accept: 'Accept',
        rerequest: 'Reject',
      },
    },
    discussion: {
      discussion: 'Discussion',
    },
    tabs: {
      overview: 'Overview',
      assessment: 'Assessment',
      externalMonitoring: 'External monitoring',
      publicData: 'Public data',
      documents: 'Documents',
      discussions: 'Discussions',
      activityFeed: 'Activity',
      about: 'About',
      notes: 'Private notes',
      emergingThreats: 'Emerging Threats',
      risks: 'Risks',
    },
    export: {
      downloadAssessment: 'Export report',
      error: 'Could not download assessment. Please try again.',
      asPdf: 'Export as PDF',
      asCsv: 'Export as CSV',
      csv: {
        assessmentType: 'Assessment type',
        clientManaged: 'Client-managed',
        domainLetter: 'Domain letter',
        domainName: 'Domain name',
        controlNumber: 'Control number',
        controlQuestion: 'Control question',
        answer: 'Answer',
        answerNotes: 'Answer notes',
        requirement: 'Requirement',
        compliant: 'Compliant',
        complianceStatus: 'Compliance status',
        overrideApplied: 'Override applied',
        clientNotes: 'Client notes',
        evidence: 'Evidence',
        lastModified: 'Last modified',
        remediation: 'Remediation',
        no: 'NO',
        yes: 'YES',
        notRequired: 'NOT REQUIRED',
        requires: 'REQUIRES {0}',
        exemption: 'EXEMPTION - {0}',
        nonCompliance: 'NON COMPLIANCE - {0}',
        atMax: '{0} OR LESS',
        atLeast: '{0} OR MORE',
        outOfScope: 'OUT OF SCOPE',
        unanswered: 'UNANSWERED',
        notApplicable: 'NOT APPLICABLE',
        awaitingResponse: 'AWAITING RESPONSE',
        awaitingReview: 'AWAITING REVIEW',
        compliantScore: 'COMPLIANT',
        notCompliantScore: 'NOT COMPLIANT',
        notScored: 'NOT SCORED',
      },
      pdfModal: {
        title: 'Export PDF report',
        subtitle:
          "Select which fields you would like to include in the report below, and then click 'Export PDF'.",
        cancel: 'Cancel',
        export: 'Export PDF',
        fieldsLabel: 'Fields to include',
        tags: 'Criticality, confidentiality & PII',
        labels: 'Custom labels',
        compliance: 'Compliance',
        remediations: 'Remediation requests',
        requirements: 'Policy requirements',
        overrides: 'Overrides',
        clientAnswerNotes: 'Client answer notes',
        privateNotes: 'Private notes',
        status: 'Approval status',
        newPagePerDomain: 'Page break between domains',
        evidence: 'Evidence',
        customProperties: 'Custom properties',
        risks: 'Risks',
        hideOutOfScope: 'Hide out of scope answers',
        generateError: 'Failed to generate PDF report.',
      },
      pdf: {
        title: 'Supplier assessment report',
        clientManaged: 'Client-managed assessment',
        clientManagedBanner:
          'Recorded by {0} from information they hold. Not submitted by {1} or verified through the Risk Ledger network.',
        criticality: 'Criticality',
        confidentiality: 'Confidentiality',
        pii: 'PII',
        labels: 'Labels',
        date: 'Date',
        dateFormat: 'yyyy/MM/dd',
        compliance: 'Compliance',
        remediations: 'Remediations',
        pendingRemediations: '{0} pending',
        respondedRemediations: '{0} responded',
        remediationRespondedAt: 'Responded: {0}',
        status: 'Status',
        scopingQuestion: 'Scoping question:',
        scopingNotes: 'Scoping notes:',
        answerResult: 'Answer: {0}',
        yes: 'Yes',
        no: 'No',
        question: 'Question',
        answer: 'Answer',
        requires: 'Requires',
        notRequired: 'Not required',
        orMore: 'or more',
        orLess: 'or less',
        outOfScope: 'Out of scope',
        na: 'N/A',
        unanswered: 'Unanswered',
        compliant: 'Compliant',
        nonCompliant: 'Non-compliant',
        notCompliant: 'Not Compliant',
        notScored: 'Not Scored',
        numberNonCompliant: '# Non-compliant controls:',
        answerNotes: 'Answer notes:',
        clientNotes: 'Client notes:',
        remediation: 'Remediation:',
        override: 'Override:',
        exemption: 'Exemption applied: {0}\nCreated on: {1}',
        nonCompliance: 'Non compliance applied: {0}\nCreated on: {1}',
        lastModified: 'Last modified:',
        rl: 'Risk Ledger',
        remediationContent: '{0}\nDeadline: {1}',
        evidence: 'Evidence:',
        reReviewDueAt: 'Re-review due',
        reassessmentNextDueAt: 'Reassessment due',
        exemptions: 'Exemptions applied',
        nonCompliances: 'Non compliances applied',
        privateNotes: 'Private notes',
        createdAt: 'Date',
        includedEvidence: 'Included evidence',
        note: 'Note',
        customProperties: 'Custom properties',
      },
    },
    evidence: {
      sidebar: {
        supplierEvidence: 'Supplier evidence',
        yourUploads: 'Your uploads',
        uploadedDocuments: 'Uploaded documents',
        certificates: 'Certifications',
        certificatesPlaceholder: 'Certifications content coming soon.',
      },
      supplierEvidence: {
        title: 'Supplier evidence',
        subtitle: 'All evidence uploaded by your supplier via the assessment controls',
      },
      certificates: {
        subtitle: 'See which security certifications your supplier maintains',
        notFound: 'No certifications found yet',
        notFoundDescription:
          'They may be listed under a different name, such as a parent company or trading name.',
        flagMissing: 'Flag missing certifications',
        disabledTitle: 'Certification discovery is not enabled',
        disabledSubtitle:
          'Enable certification discovery from the overview tab to see results here.',
        valid: 'Valid',
        expired: 'Expired',
        expiryDate: 'Expiry date',
        noExpiry: 'No date available',
        accreditationBody: 'Accreditation Body',
        certificationBody: 'Certification Body',
        source: 'Source',
        sourceType: 'Source type',
        certificationId: 'Certification ID',
        matchedCertifiedEntityName: 'Certified entity name',
        scopeUnknown: 'The scope of this certification could not be determined.',
        wholeOrganisation:
          'This certification applies to the whole organisation, covering all IT operations and information systems under its management.',
      },
      entity: 'Evidence',
      searchPlaceholder: 'Search evidence',
      noResultsTitle: 'No matching evidence',
      noResultsSearchSubtitle: 'Try a different search',
      noResultsFilterSubtitle: 'Try a different filter',
      noResultsSearchFilterSubtitle: 'Try a different search or filter',
      actions: 'Actions for {name}',
      preview: 'Preview',
      download: 'Download',
      bulkActions: 'Actions',
      bulkDownload: 'Download selected',
      placeholder: {
        title: 'No evidence yet',
        subtitle:
          'Evidence appears here when this supplier attaches files to their assessment answers.',
        errorTitle: 'Supplier evidence',
        errorDescription: 'Failed fetching supplier evidence',
      },
      filter: {
        domainControl: 'Domain / control',
        lastUploaded: 'Last uploaded',
        sortBy: 'Sort by',
        filterBy: 'Filter by',
        clearFilters: 'Reset',
      },
      filterAttributes: {
        name: 'Name',
        extension: 'File extension',
        size: 'File size',
        domain: 'Domain',
      },
      control: 'Control',
      uploaded: 'Uploaded at',
      expiredOn: 'Expired on {date}',
      expiresOn: 'Expires on {date}',
      tableColumns: {
        name: 'Name',
        extension: 'File extension',
        size: 'File size',
        uploadedAt: 'Upload date',
        createdAt: 'Date created',
        modifiedAt: 'Date modified',
        controlDomain: 'Control / domain',
        attachments: 'Attachments',
      },
      emergingThreatQuestion: 'Emerging Threat q.{index}',
    },
    userLabel: {
      team: 'Team',
      role: 'Role',
      restrictedSupplierAccess: 'They can only access their assigned suppliers.',
      restrictedSupplierAccessSelf: 'You can only access your assigned suppliers.',
    },
    connect: {
      title: 'Connect with {name}',
      description:
        'To help {orgName} verify your connection request, include the name of your account manager or another identifier such as an account number in the message below.',
      noContactDescription:
        "We know that sometimes you won't have a named contact of a supplier. If that is the case, please select the reason below so that we can better assist you.",
      sentTitle: 'Connection sent',
      sentDescription: "You've requested access to their full profile & assessment.",
      sentContent: '{email} has received an email about your request.',
      nameLabel: 'Contact name',
      namePlaceholder: 'Jane doe',
      emailLabel: 'Contact email address',
      emailPlaceholder: "jane{'@'}example.com",
      messageLabel: 'Message',
      messagePlaceholder:
        "Example: We'd like to connect with you on Risk Ledger to manage our third-party risk assessment.",
      contactMode: {
        question: 'How would you like to send this connection request?',
        provide: 'Invite a contact now',
        noContact: 'Add without a contact',
      },
      noContactReasonLabel: "Reason you don't have supplier contact details",
      noContactReasonLabelClient: "Reason you don't have client contact details",
      // Unclaimed orgs have no users, so with no contact the request reaches no one.
      // Claimed orgs are notified regardless, so the contact only adds a direct email.
      noContactReasonInfoUnclaimed:
        'Without a contact, no one can claim this supplier or complete its assessment. Add one later when you have their details.',
      noContactReasonInfoClaimed:
        "By including contact details, you are 64% more likely to get a reply. This gets the right person's attention faster, and helps our team follow up on your behalf.",
      otherReasonLabel: 'Please specify',
      otherReasonPlaceholder: 'Why do you not have supplier contact details?',
      otherReasonPlaceholderSupplierConnect:
        "Tell us why you don't have the supplier's contact details.",
      emailRequired: 'Enter the contact email address.',
      noContactReasonRequired: 'Select a reason before sending.',
      nameRequired: 'Enter the contact name',
      otherReasonRequired: 'Enter a reason',
      cancel: 'Cancel',
      connect: 'Connect',
      viewBasicProfile: 'View basic profile',
      primerEmail: {
        desc: 'I confirm a {primerEmail} has been sent to this supplier.',
        link: 'Primer email',
      },
      messageAutofillShort:
        'Hi - {senderOrg} would like to connect with you to review your Risk Ledger assessment. Thanks, {senderUser} ({senderEmail})',
      messageAutofillLong:
        'Hi - {senderOrg} would like to connect with you to review your Risk Ledger assessment. We emailed {contact} on [date] letting them know of this request. Thanks, {senderUser} ({senderEmail})',
      addDate: 'Please add the date of contact or modify the message.',
      connectionIDError: 'An error occurred. Refresh the page and try again.',
      categoriseTitle: 'How would you categorise this supplier?',
      categoriseDescription:
        'Categorising your supplier now will make sure that when they fill in their security assessment, they will be assessed against your current policies.',
      finish: 'Finish',
    },
    connectionFlow: {
      modalTitle: {
        addSupplier: 'Add supplier',
        addClient: 'Add client',
        inviteSupplier: 'Invite supplier',
        requestAccess: 'Request access',
      },
      steps: {
        supplierDetails: 'Supplier details',
        contactDetails: 'Contact details',
        assessmentScope: 'Assessment scope',
        supplierClassification: 'Supplier classification',
      },
      actions: {
        addSupplier: 'Add supplier',
      },
      cancelModal: {
        title: 'Cancel connection?',
        description:
          'Closing this will stop the connection request. Any information entered will be lost.',
        keepEditing: 'Keep editing',
        discardRequest: 'Discard request',
      },
      supplierDetails: {
        locationLabel: 'Location',
        supplierNameRequired: 'Enter the supplier name',
        websiteRequired: 'Enter the supplier website',
        locationRequired: 'Select a location',
      },
      classification: {
        unknown: 'Unknown',
        pipelineStatusLabel: 'Relationship (pipeline status)',
        pipelineStatusSubtitle: 'Track where this supplier sits in your procurement lifecycle.',
        pipelineStatusPlaceholder: 'Select status',
        pipelineStatusRequired: 'Select a status before continuing.',
      },
      flexible: {
        standardScopeHeading: 'Standard scope',
        classificationScopeHeading: 'Scope based on classification',
        optionTitle: 'Policy-driven',
        controlCount: '{n} controls | {n} control | {n} controls',
        basedOnPolicies: 'based on {n} policies | based on {n} policy | based on {n} policies',
        disabledNoControls:
          "Policy-driven scope is unavailable, since you haven't selected any labels & tags.",
        validationNoControls:
          'Policy-driven scope requires labels or tags that resolve to at least one control.',
      },
      restrictedOptionsNotice: {
        title: "Some options aren't available because of your permissions",
        description:
          'Contact your admin to update these options. You can still send a connection request.',
      },
      orchestrationErrors: {
        framework: 'Failed to apply framework settings. Update on the supplier profile page.',
        products: 'Failed to apply product scope. Update on the supplier profile page.',
        tags: 'Failed to apply supplier tags. Update on the supplier profile page.',
        labels: 'Failed to apply supplier labels. Update on the supplier profile page.',
      },
      errors: {
        createSupplier: 'Failed to add supplier. Please try again.',
        resolveConnection: 'Failed to create connection. Please try again.',
        applyFramework: 'Failed to apply framework settings. Update on the supplier profile page.',
        applyProducts: 'Failed to apply product scope. Update on the supplier profile page.',
        applyTags: 'Failed to apply supplier tags. Update on the supplier profile page.',
        applyLabels: 'Failed to apply supplier labels. Update on the supplier profile page.',
        applyPipelineStatus:
          'Failed to apply pipeline status. Update on the supplier profile page.',
      },
      classificationRequiresSupplier:
        'Supplier classification can be completed once a supplier context is available.',
    },
    suppliers: {
      title: 'Add & invite supplier',
      name: 'Supplier name',
      namePlaceholder: 'Supplier name',
      website: 'Website',
      websitePlaceholder: 'https://example.com',
      companyNumber: 'Registered company number',
      companyNumberPlaceholder: 'eg. 10203040',
      industry: 'Sector',
      industryPlaceholder: 'Select sector',
      country: 'Registered country',
      countryPlaceholder: 'Select country',
      moreInfo: 'This will need to be verified by the supplier and may change after they sign up.',
      contactInfo:
        "If you have a contact at this supplier, we'll invite them to the Risk Ledger platform to complete an assessment.",
      contactName: 'Contact name',
      contactNamePlaceholder: 'Jane doe',
      contactEmail: 'Contact email address',
      contactEmailPlaceholder: "jane{'@'}example.com",
      nameRequired: 'Enter the supplier name',
      websiteRequired: 'Enter the supplier website',
      countryRequired: 'Select a registered country',
      contactEmailRequired: 'Enter a contact email address',
      primerEmail: {
        desc: 'I confirm a {primerEmail} has been sent to this supplier.',
        link: 'Primer email',
      },
      nextStep: 'Next step',
      addAndInvite: 'Add & invite supplier',
      addSuccess: 'Supplier added successfully.',
      invitationSentSuccess: 'Invitation sent successfully',
      skipInvite: 'Skip invite for now',
      step1: 'Set supplier information',
      step2: 'Invite to Risk Ledger',
      connectionExistsError:
        'The contact email is already in use by another supplier you are connected to.',
      addSupplierFailedError:
        "We couldn't add this supplier. If this keeps happening, please contact support.",
      inviteError: 'Failed to add supplier. Please try again.',
      inviteAlreadySentError:
        'An invitation has already been sent to this supplier. You can manage it in the Action Centre.',
      orgExists:
        "This organisation already exists. If it's not listed as a supplier, contact support to connect. Otherwise, try searching again.",
      orgExistsCheckFailed: "We couldn't validate this organisation right now. Please try again.",
    },
    connectionProducts: {
      title: {
        client: 'Products',
        supplier: 'Products this client uses',
      },
      edit: {
        client: 'Edit products',
        supplier: 'Edit products this client uses',
      },
      noProducts: {
        client: 'No products have been added by this supplier',
        supplier: 'No products have been added by your organisation',
      },
      noneSelected: {
        client: `Your organisation is not connected to any of this supplier's products`,
        supplier: 'This client is not connected to any of your products',
      },
      loading: 'Loading...',
      error: 'Failed to load products. Please try again.',
      modal: {
        client: {
          title: 'Change the products you see from this supplier',
          description:
            'Keeping the products you use accurate from this supplier will help you see accurate answers to your assessment questions. ',
          label: 'Products or services from this supplier you use',
          noProducts: 'No products have been added by this supplier',
          noMoreProducts: 'No more products have been added by this supplier',
        },
        supplier: {
          title: 'Change the products this client is connected to',
          description:
            'It is important to make sure that you give the client answers relating to the services they use. Please make sure below that they receive the correct answers by settings the answers they see. ',
          label: 'Products or services this client uses',
          noProducts: 'No products found',
        },
        emptyState: 'No products selected',
        addProducts: 'Add more products',
      },
    },
    levelRequired: {
      title: 'Framework size required',
      edit: 'Edit framework size required',
      policyDriven: 'Policy-driven',
      error: 'Failed to update framework config. Please try again.',
    },
    addOnsRequired: {
      title: 'Add-ons required',
      edit: 'Edit add-ons required',
      none: 'None',
      notApplicable: 'Not applicable for policy-driven scope',
    },
    assessmentScopeRequired: {
      title: 'Assessment scope',
      edit: 'Edit assessment scope',
      scopeRequired: 'Scope required',
      addOnsRequired: 'Add-ons required',
      controlCount: '{n} controls | {n} control | {n} controls',
    },
    editFrameworkConfigModal: {
      introTitle: 'What information do you want from this supplier?',
      introBody: {
        reason:
          'Choose the purpose of this connection, and select the framework size and add-on domains the supplier should complete. These selections determine which controls you can review.',
        noReason:
          'Choose the framework size and add-on domains the supplier should complete. These selections determine which controls you can review.',
      },
      addOnsTitle: 'Add-on domains',
      addOnsDescription:
        "Select any additional domains you'd like this supplier to fill out above and beyond the core compliance framework.",
      levelsTitle: 'Framework size',
      levelsDescription: 'Set the size of framework you want the supplier to fill out.',
      levelRequired: 'Select a framework size',
      productAnswersTitle: 'Product answers',
      productAnswersDescription:
        'This supplier has added product-specific answers to some questions. Select any products you use from this supplier.',
    },
    categoriseSupplierModal: {
      modalTitle: 'What information do you want the supplier to complete?',
      modalDescription:
        'Setting the framework size and add-ons you want a supplier to fill out will determine which controls you will be able to review them against',
      levelTitle: 'Framework size',
      levelDescription: 'Set the size of framework you want the supplier to fill out.',
      addOnsTitle: 'Add-on domains',
      optional: '(optional)',
      addOnsDescription:
        "Select any additional domains you'd like this supplier to fill out above and beyond the core compliance framework.",
    },
    editAddOnsModal: {
      introTitle: 'What add-on domains do you want the supplier to complete?',
      introBody: 'Select the add-on domains you would like to review this supplier against',
      optionsTitle: 'Add-on domains',
      confirmTitle: 'Are you sure you want to make this change?',
      addTitle: 'Adding an add-on will:',
      add1: 'Notify the supplier to complete the newly required questions.',
      add2: "Immediately affect the supplier's compliance score, based on the add-on's controls, regardless of whether they have answered the questions.",
      removeTitle: 'Removing an add-on will:',
      remove1: 'Close any risks that are associated to the controls within this add-on.',
      remove2: 'Archive any discussions that are associated to the controls within this add-on.',
      remove3: 'Close any remediations that are associated to the controls within this add-on.',
    },
    editLevelsModal: {
      introTitle: 'What framework size do you want the supplier to complete?',
      introBody: 'Select the framework size you would like to review this supplier against',
      optionsTitle: 'Framework size',
      smallerTitle: 'This will:',
      smaller1: 'Close any risks associated with controls not present in this framework.',
      smaller2: 'Archive any discussions related to controls not present in this framework.',
      smaller3: 'Close any remediations linked to controls not present in this framework.',
      largerTitle: 'Requiring the larger framework will:',
      larger1: 'Notify the supplier to complete the new framework size.',
      larger2:
        "Immediately affect the supplier's compliance score, regardless of whether they have answered the questions.",
      confirmTitle: 'Are you sure you want to make this change?',
    },
    editAssessmentScopeModal: {
      introTitle: 'Change assessment scope',
      introBody:
        'Change the assessment scope required to see what you want your supplier to answer in their security assessment',
      addOnDomainsOptional: 'Add-on domains (optional)',
      additionalControls:
        '{n} additional controls | {n} additional control | {n} additional controls',
      impactTitle: 'Change assessment scope',
      impactIntro:
        'Based on your new assessment scope, the following changes will happen on your controls:',
      controlsRequired: 'Controls required',
      existingRelationsTitle: 'When changing to a different framework, it will:',
      existingRelations1: 'Close risks on controls no longer in scope',
      existingRelations2: 'Archive discussions on controls no longer in scope',
      existingRelations3: 'Close remediations on controls no longer in scope',
      error: 'Could not update the assessment scope. Please try again.',
    },
    assessmentBanners: {
      assignedInDomain:
        'You are assigned to review {n} control in this domain | You are assigned to review {n} controls in this domain',
      assignedAllDomains:
        'You are assigned to review {n} control | You are assigned to review {n} controls',
      viewControls: 'View controls',
    },
    aiAssessmentReview: {
      startTitle: 'Use AI to review this assessment and find issues',
      startButton: 'Start',
      generatingTitle: 'Reviewing this assessment with AI',
      scannedTitleDomain:
        'There is {n} finding on this domain | There are {n} findings on this domain',
      scannedTitleAllQuestions:
        'There is {n} finding on this assessment | There are {n} findings on this assessment',
      scannedTitleDomainFiltered:
        'There is {n} of the {total} findings on this domain matching the filters | There are {n} of the {total} findings on this domain matching the filters',
      scannedTitleAllQuestionsFiltered:
        'There is {n} of the {total} findings on this assessment matching the filters | There are {n} of the {total} findings on this assessment matching the filters',
      show: 'Show',
      clear: 'Clear',
      domainFailed: 'This domain could not be scanned',
      errorTitle: "We couldn't review this assessment",
      tabLabel: 'Findings (AI)',
      noFindings: 'No findings for this control',
      evidenceTitle: 'Finding evidence',
      recommendedActionTitle: 'Recommended action',
      possibleActions: 'Possible actions',
      viewEvidence: 'View evidence',
      dismiss: 'Dismiss this finding',
      dismissShort: 'Dismiss',
      undoDismiss: 'Undo',
      dismissError: 'Failed to dismiss finding. Try again or contact support.',
      undoDismissError: 'Failed to restore finding. Try again or contact support.',
      dismissedMessage: 'This finding has been dismissed',
      rationaleLabel: 'Why we recommend this',
      feedbackSuccess: 'Successfully submitted feedback',
      hoverTitle: {
        discussion: 'AI finding: Discussion recommended',
        remediation: 'AI finding: Remediation recommended',
        risk: 'AI finding: Risk recommended',
      },
      findingType: {
        missing_response: 'Missing response',
        weak_evidence: 'Weak evidence',
        policy_gap: 'Policy gap',
        insufficient_information: 'Insufficient information',
        not_compliant: 'Not compliant',
      },
      recommendation: {
        discussion: 'Start a discussion',
        remediation: 'Send a remediation request',
        risk: 'Raise a risk',
      },
      shortcut: {
        discussion: 'Start discussion',
        remediation: 'Request remediation',
        risk: 'Open risk',
      },
    },
    assessmentSidebar: {
      inscopeDomains: 'In-scope domains',
      outofscopeDomains: 'Out-of-scope domains',
    },
    assessmentHero: {
      allQuestions: 'All questions',
      tooltips: {
        domainCompliance: 'Number of compliant controls in this domain',
        allQuestionsCompliance: 'Compliance score',
        discussions: 'Filter by discussions',
        privateNotes: 'Filter by private notes',
        remediations: 'Filter by remediation requests',
        risks: 'Filter by risks',
        overrides: 'Filter by overrides',
        communitySignals: 'Filter by community signals',
      },
      actionLabel: '{action}: {count}',
    },
    assessmentTable: {
      actions: {
        title: 'Actions',
        markAsCompliant: 'Mark as compliant',
        markAsNonCompliant: 'Mark as non-compliant',
        markAsReviewed: 'Mark as reviewed',
        markAsNotReviewed: 'Mark as not reviewed',
        assignUsers: 'Assign users',
        assign: {
          confirmRemoveWithTasksTitle: 'Remove assignee?',
          confirmRemoveWithTasksDescription:
            'One or more of the selected users has already reviewed. Removing their assignments will remove their review.',
        },
        removeAssignment: 'Remove assignment',
        remove: 'Remove',
        errors: {
          markAsCompliant:
            "We couldn't mark the selected controls as compliant. Try again or contact support.",
          markAsNonCompliant:
            "We couldn't mark the selected controls as non-compliant. Try again or contact support.",
          markAsReviewed:
            "We couldn't mark the selected questions as reviewed. Try again or contact support.",
          markAsNotReviewed:
            "We couldn't mark the selected questions as not reviewed. Try again or contact support.",
          assignUsers:
            "We couldn't assign users to the selected questions. Try again or contact support.",
          removeAssignment:
            "We couldn't remove assignments from the selected questions. Try again or contact support.",
        },
      },
    },
    assessmentFilters: {
      entityName: 'Question',
      searchPlaceholder: 'Search a question...',
      controlAnswerText: 'Question, Description, or Notes',
      requirement: {
        name: 'Policy Requirement',
      },
      scope: {
        name: 'Scope',
        inScope: 'In scope',
        outOfScope: 'Out of scope',
      },
      compliance: {
        name: 'Compliance',
        compliant: 'Compliant',
        notCompliant: 'Not compliant',
        notScored: 'Not scored',
      },
      override: {
        name: 'Override',
        exemption: 'Exemption',
        nonCompliance: 'Non-compliance',
      },
      remediation: {
        name: 'Remediation',
        pending: 'Pending',
        responded: 'Responded',
        community: 'Community Opened',
      },
      risks: {
        name: 'Risk',
        opened: 'Has opened risk',
        noOpened: 'Has no opened risk',
        community: 'Has community risk',
      },
      product: 'Product',
      orgAnswers: 'Organisation answers',
      certification: 'Certification',
      discussion: {
        name: 'Discussion',
        noDiscussion: 'Has no discussion',
        hasDiscussion: 'Has discussion',
      },
      notes: {
        name: 'Private notes',
        noNotes: 'Has no private notes',
        hasNotes: 'Has private notes',
      },
      reviewed: {
        name: 'Reviewed',
        isReviewed: 'Has been reviewed',
        notReviewed: 'Has not been reviewed',
      },
      reviewedBy: {
        name: 'Reviewed by',
      },
      assigned: {
        name: 'Assigned',
        isAssigned: 'Has been assigned',
        notAssigned: 'Has not been assigned',
      },
      assignedTo: {
        name: 'Assigned to',
      },
      modifiedAt: 'Last modified',
      control: {
        name: 'Control',
      },
      aiFindings: {
        name: 'AI findings',
        noFindings: 'Has no AI findings',
        hasFindings: 'Has AI findings',
      },
      aiAction: {
        name: 'AI recommended action',
        noAction: 'No action',
        discussion: 'Discussion',
        remediation: 'Remediation',
        risk: 'Risk',
      },
      communitySignal: {
        name: 'Community signals',
        noSignals: 'Has no community signals',
        hasSignals: 'Has community signals',
      },
      communitySignalType: {
        name: 'Community signal type',
        risk: 'Risk',
        controlNote: 'Control note',
        remediation: 'Remediation',
        override: 'Override',
      },
    },
    assessmentTab: {
      tooltips: {
        discussion: 'Has discussion',
        privateNoteTitle: 'Private note',
        reviewed: 'Reviewed by {name}',
        assigned: 'Assigned to {name}',
        risksTitle: 'Risks',
        remediation: 'Remediation',
        override: 'Override',
        aiFinding: 'AI review finding',
        communitySignals: '{n} community signal | {n} community signals',
      },
      cols: {
        question: 'Question',
        answer: 'Answer',
        compliance: 'Compliance',
        modifiedAt: 'Modified',
      },
      scopingQuestion: 'Scoping question',
      questionCount: '{n} question | {n} questions',
      childFollowUp: '{parentName} follow-up',
      productAnswer: 'Relates to {productName}',
      notes: 'Supplier notes',
      evidence: 'Evidence',
      selected: 'Selected',
      empty: 'No relevant answers',
    },
    assessmentDrawer: {
      label: 'Answer details',
      tabs: {
        details: 'Details',
        notes: 'Private notes',
        activity: 'Activity',
        discussions: 'Discussions',
        communitySignals: 'Community signals',
      },
      setCompliant: 'Set to compliant',
      setNotCompliant: 'Set to non-compliant',
      overrideApplied: 'Overridden on {date}',
      overrideCompliance: 'Override compliance',
      removeOverrideError: 'Failed to remove override. Please try again.',
      answer: 'Answer',
      notes: 'Notes',
      showMore: 'Show more',
      showLess: 'Show less',
      modified: 'Last modified',
      lastConfirmed: 'Last confirmed',
      evidenceAttached: 'Evidence',
      attachedOn: 'Added {date}',
      previewEvidence: 'Preview {title}',
      relatedScans: 'Related scans',
      remediationRequested: 'Remediation',
      remediationDeadline: 'Deadline {date}',
      remediationCreated: 'Request raised',
      cancelRemediation: 'Cancel request',
      risks: 'Risks',
      addRisk: 'Open risk',
      raiseRemediation: 'Request remediation',
      remediatedAt: 'Remediated by supplier on {date}',
      cancelRemediationError: 'Failed to cancel remediation. Please try again.',
      rejectRemediationError: 'Failed to reject remediation. Please try again.',
      acceptRemediationError: 'Failed to accept remediation. Please try again.',
      relatedPolicies: 'Related policies',
      policiesTitle: 'Policies impacting compliance',
      reviewers: 'Reviewers',
      markAsReviewed: 'Mark as reviewed',
      markAsNotReviewed: 'Mark as not reviewed',
      you: 'You',
      reviewed: 'Reviewed',
      notReviewed: 'Not reviewed',
      noReviewers: 'No reviewers',
      errorCreatingReviews: 'Failed to mark as reviewed. Please try again.',
      errorDeletingReviews: 'Failed to mark as not reviewed. Please try again.',
      manageAssignments: 'Manage reviewers',
      addAssignee: 'Add assignee',
      removeAssignee: 'Remove',
      confirmRemoveAssigneeWithTasksTitle: 'Remove assignee?',
      confirmRemoveAssigneeWithTasksDescription:
        "You've selected a colleague that's already marked one or more controls as reviewed. Unassigning them will remove their reviews",
      errorAssigningUser: 'Failed to assign user. Please try again.',
      errorRemovingAssignment: 'Failed to remove assignment. Please try again.',
    },
    lastSignedIn: {
      label: 'Last sign in',
      withinWeek: 'Less than a week ago',
      weeks: '{n} week ago | {n} weeks ago',
      months: '{n} month ago | {n} months ago',
      overAYear: 'More than a year ago',
      never: 'Never',
      help: "The most recent sign-in by anyone at this supplier, across the whole platform rather than just your connection. It doesn't mean they've responded to you",
      helpNever:
        "This supplier has claimed their profile but hasn't signed in since. Reaching a different contact is more likely to work than another chaser",
    },
    suggestContact: {
      trigger: 'Suggest a contact',
      title: 'Suggest a new contact',
      explainer:
        "We'll reach out to this contact to begin the process of handing over this supplier account.",
      timingNote: "This can take a few days. We'll reach out if we need more information.",
      nameLabel: 'Name',
      namePlaceholder: 'Jane Smith',
      emailLabel: 'Email',
      emailPlaceholder: "jane{'@'}example.com",
      nameRequired: 'Enter the contact name',
      emailRequired: 'Enter the contact email address',
      messageLabel: 'Message',
      messagePlaceholder: 'Example: Priya took over from Jane as our main contact there',
      cancel: 'Cancel',
      submit: 'Suggest contact',
      submitted: 'Contact suggested',
      submitFailed: 'Failed to suggest contact. Please try again.',
    },
    clientManagedAssessment: {
      complianceOverview: 'Compliance Overview',
      startTitle: 'Waiting on {supplier} to submit',
      startText:
        'While you wait for {supplier} to submit their assessment, answer it yourself to build a compliance view now, and switch to their answers once they do.',
      start: 'Start answering',
      startFailed: "Couldn't start the assessment. Try again.",
      continueTitle: "Keep answering on {supplier}'s behalf",
      continueText:
        'Finish the assessment to build a compliance view, and switch to their answers when they submit.',
      continueTextOverdue:
        'Their own answers are out of date. Finish yours to build a current compliance view, and switch to theirs once they reassess.',
      continueTextSupplierReady:
        'Their own answers are ready. Switch to them from the Assessment tab.',
      progress: '{percentage}% complete',
      continue: 'Continue answering',
      banner:
        "These are your answers, filled in on {supplier}'s behalf. Edit them any time, or switch to theirs when they submit.",
      editAnswers: 'Edit answers',
      overdueBanner:
        "These are your answers, filled in on {supplier}'s behalf because theirs are out of date. Edit them any time, or switch to theirs once they reassess.",
      supplierSubmittedBanner:
        'Their own answers are ready. As the verified source, they give the most accurate security posture.',
      switchToSupplier: 'Switch to their answers',
      switchToSupplierModal: {
        whatChanges: 'What changes',
        liveReview: "Supplier's verified answers become the live review",
        reviewKept: 'Your client-managed review is kept and stays viewable',
        whatItAffects: 'What this affects',
        risksAndNotes: 'Your risks and control notes stay, now shown against their answers',
        overrides: 'Any overrides you made are kept, and may need reviewing against their answers',
      },
      switchedToSupplier:
        'Switched to their answers. Your review now shows {count} answer. | Switched to their answers. Your review now shows {count} answers.',
      switchToClient: 'Switch to client managed',
      switchedToClient:
        'Switched to your answers. Your review now shows {count} answer. | Switched to your answers. Your review now shows {count} answers.',
      switchFailed: "Couldn't switch answers. Try again.",
      overdueTitle: 'Reassessment overdue',
      overdueDue: 'Due {date}',
      overdueText:
        'Supplier answers are out of date, so their compliance score may not be accurate.',
      answerOnTheirBehalf: 'Answer on their behalf',
      switchToYourAnswers: 'Switch to your answers',
    },
  },
};
